import { differenceInMilliseconds } from "date-fns";
import type { IGanttController } from "elements/gantt/controller";
import { IGanttBaseCellController } from "elements/gantt/controllers/base-cell-controller";
import { combineMappingWithData } from "elements/gantt/controllers/controller-utils";
import { useColumnConfiguration } from "elements/gantt/hooks/use-column-configuration";
import { getMondayItemId, hasDateConfigInMetadata, isMondayGanttElement } from "elements/gantt/utils";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { BoardIntegrationLoadingState, MondayItem } from "frontend/hooks/use-board-integrations.types";
import { equals, groupBy, isEmpty } from "rambda";
import { useEffect, useRef, useState } from "react";
import { IntegrationItem } from "shared/datamodel/schemas";
import { MondayTaskElement } from "shared/datamodel/schemas/monday-task";
import { canvasMetadataPrefix } from "shared/util/utils";
import { useRawCanvasMetadata } from "subscriptions";

type MondayGanttElement = IntegrationItem | MondayTaskElement;

export const useGanttSyncMondayItems = ({ controller }: { controller: IGanttController }) => {
  const metadata = useRawCanvasMetadata(controller.context.reflect);
  const findExistingMapping = useColumnConfiguration({
    reflect: controller.context.reflect,
  });
  const [mondayTasks, setMondayTasks] = useState<{ [index: string]: IGanttBaseCellController<MondayGanttElement>[] }>(
    {}
  );
  const pusherChannel = controller.context.pusherChannel;
  const { dataSource, loadingBoardIntegrationsState, fetchItems, getItem, getLoadingState } = useBoardTasks(
    controller.context.documentId,
    pusherChannel
  );

  const fetchLoadingItemsRef = useRef(new Set<string>());
  useEffect(() => {
    const setTasks = () => {
      const tasks = controller
        .getTaskCells()
        .filter((t) => isMondayGanttElement(t.element)) as IGanttBaseCellController<MondayGanttElement>[];

      const tasksByIntegratinId = groupBy(
        (task: IGanttBaseCellController<MondayGanttElement>) => task.element.integrationId
      )(tasks);
      if (!equals(tasksByIntegratinId, mondayTasks) && !isEmpty(tasksByIntegratinId)) {
        setMondayTasks(tasksByIntegratinId);
      }
    };
    controller.subscribe(setTasks);

    return () => controller.unsubscribe(setTasks);
  }, [controller, mondayTasks]);

  useEffect(() => {
    if (!isEmpty(dataSource) && !isEmpty(mondayTasks)) {
      for (const [integrationId, { items, boardId }] of Object.entries(dataSource)) {
        if (!items || !Array.isArray(items)) continue;

        const integrationMetadata = metadata.find(([id]) => id === `${canvasMetadataPrefix}${boardId}`);
        const tasks = mondayTasks[integrationId] ?? [];
        for (const task of tasks) {
          const item = items.find((item) => item.id === getMondayItemId(task.element));
          const integrationData = dataSource[integrationId];
          // Early returns for cases where we can't proceed
          if (!item) {
            if (integrationData?.items.length === 0 && integrationData?.error === "Failed getting integration items") {
              patchError(controller, task.id, "failed-loading");
            }
            continue;
          }

          // Handle case with item but no integration metadata
          if (!integrationMetadata) {
            patchTitleOnly(controller, task.id, item.name);
            task.updateIntegrationConfig?.({
              url: item.url,
            });

            continue;
          }

          const taskConfig = integrationMetadata[1].boardColumnsMapping;
          if (!taskConfig) {
            task.updateIntegrationConfig?.();
            continue;
          }

          const newTaskData = combineMappingWithData(taskConfig as Record<string, string>, item);

          // Handle initial loading state
          if (task.isInitialLoading?.()) {
            updateTaskForInitialLoad(controller, task.id, newTaskData);
          } else {
            patchTitleOnly(controller, task.id, newTaskData.title);
          }

          // Handle integration config updates
          updateIntegrationConfiguration(task, taskConfig, newTaskData, item);
        }
      }
      controller.notify();
    } else {
      if (loadingBoardIntegrationsState === BoardIntegrationLoadingState.loaded) {
        const integrationIds = Object.keys(mondayTasks);
        for (const integrationId of integrationIds) {
          const loadingState = getLoadingState(integrationId);
          if (loadingState === BoardIntegrationLoadingState.notLoaded) {
            fetchItems({
              [integrationId]: {
                itemIds: mondayTasks[integrationId].map((task) => getMondayItemId(task.element)) as string[],
              },
            });
          } else if (
            loadingState === BoardIntegrationLoadingState.loaded ||
            loadingState === BoardIntegrationLoadingState.loading
          ) {
            for (const tasks of Object.values(mondayTasks)) {
              for (const task of tasks) {
                const loadingState = getLoadingState(task.element.integrationId);
                if (loadingState === BoardIntegrationLoadingState.failed) {
                  patchError(controller, task.id, "failed-loading");
                }
              }
            }
          }
        }
      }
    }
  }, [
    controller.getTaskCells().length,
    JSON.stringify(dataSource),
    mondayTasks,
    loadingBoardIntegrationsState,
    findExistingMapping,
  ]);

  // this function is called when the task is *not* updated by the pusher
  const updateTask = async (taskId: string) => {
    const task = controller.getTaskCells().find((t) => t.id === taskId);
    if (!task?.element || !isMondayGanttElement(task.element)) {
      return;
    }
    const newData = await fetchItems({
      [task.element.integrationId]: {
        itemIds: [getMondayItemId(task.element)],
      },
    });
    if (!newData) {
      return;
    }
    const item = newData[task.element.integrationId].items?.[0] as MondayItem;
    if (!item) {
      return;
    }
    const config = task.getConfiguration?.();
    if (!config) {
      return;
    }
    const newTaskData = combineMappingWithData(
      {
        dependency: config.dependencyId,
        end_date: config.endId,
        start_date: config.startId,
        timeline: config.timelineId,
      } as Record<string, string>,
      item
    );
    if (!newTaskData) {
      return;
    }

    const currentStartDate = task.getStartDate();
    const currentEndDate = task.getEndDate();
    if (newTaskData && (newTaskData.startDate !== currentStartDate || newTaskData.endDate !== currentEndDate)) {
      controller.context.undoRedoStack?.patchElement(taskId, (draft: MondayGanttElement) => {
        draft.fieldValues ??= {};
        if (!Number.isNaN(newTaskData.startDate)) {
          draft.fieldValues["fromDate"] = newTaskData.startDate;
        }
        if (!Number.isNaN(newTaskData.endDate)) {
          draft.fieldValues["toDate"] = newTaskData.endDate;
        }
      });
    }
    fetchLoadingItemsRef.current.delete(taskId);
    patchTitleOnly(controller, taskId, newTaskData.title);
  };

  const getIntegrationTaskDates = (task: MondayGanttElement) => {
    if (findExistingMapping(task.integrationId)) {
      const item = getItem(getMondayItemId(task), task.integrationId);
      const config = findExistingMapping(task.integrationId);
      if (!config || !item) {
        return null;
      }

      const newTaskData = combineMappingWithData(
        {
          dependency: config.dependency,
          end_date: config.end_date,
          start_date: config.start_date,
          timeline: config.timeline,
          "date-tab": config["date-tab"],
        } as Record<string, string>,

        item
      );
      if (newTaskData) {
        return {
          startDate: newTaskData.startDate,
          endDate: newTaskData.endDate,
        };
      }
    }
    return null;
  };

  return { updateTask, getIntegrationTaskDates };
};

function patchTitleOnly(controller: IGanttController, taskId: string, title: string) {
  controller
    .getTaskCells()
    .find((t) => t.id === taskId)
    ?.changeTitle(title);
}

function patchError(controller: IGanttController, taskId: string, type: "failed-loading") {
  controller
    .getTaskCells()
    .find((t) => t.id === taskId)
    ?.updateError?.(type);
}

function updateTaskForInitialLoad(controller: IGanttController, taskId: string, newTaskData: any) {
  controller.context.undoRedoStack?.patchElement(taskId, (draft: MondayGanttElement) => {
    const hasJustBeenPlaced = differenceInMilliseconds(draft.fieldValues?.placedAt, Date.now()) < 100;
    draft.fieldValues ??= {};

    if (
      newTaskData.startDate &&
      newTaskData.endDate &&
      newTaskData.startDate <= newTaskData.endDate &&
      !hasJustBeenPlaced
    ) {
      if (!Number.isNaN(newTaskData.startDate)) {
        draft.fieldValues["fromDate"] = newTaskData.startDate;
      }
      if (!Number.isNaN(newTaskData.endDate)) {
        draft.fieldValues["toDate"] = newTaskData.endDate;
      }
    }
    delete draft.fieldValues["placedAt"];
  });

  patchTitleOnly(controller, taskId, newTaskData.title);
}

function updateIntegrationConfiguration(
  task: IGanttBaseCellController<MondayGanttElement>,
  taskConfig: Record<string, string>,
  newTaskData: any,
  item: any
) {
  const oldConfig = task.getConfiguration?.();
  if (!oldConfig) {
    // task.updateIntegrationConfig?.();
    return;
  }

  const { dependencyId = "", endId = "", startId = "", timelineId = "" } = oldConfig;
  const hasDateConfig = hasDateConfigInMetadata(taskConfig);

  if (!hasDateConfig) {
    task.updateIntegrationConfig?.();
    return;
  }

  const configChanged =
    dependencyId != taskConfig.dependency ||
    endId != taskConfig.end_date ||
    startId != taskConfig.start_date ||
    timelineId != taskConfig.timeline;

  const useTimelineColumn = taskConfig["date-tab"] === "timeline";

  if (configChanged) {
    task.updateIntegrationConfig?.({
      dependencies: newTaskData.dependencies,
      url: item.url || "",
      dependencyId: taskConfig.dependency,
      endId: useTimelineColumn ? undefined : taskConfig.end_date,
      startId: useTimelineColumn ? undefined : taskConfig.start_date,
      timelineId: useTimelineColumn ? taskConfig.timeline : undefined,
    });

    if (!task.isInitialLoading?.()) {
      task.updateIntegration?.("monday");
    }
  }
}
